/**
 * The app's single toast surface (sonner).
 *
 * Mounted once at the React root in main.tsx, so it covers the App shell, the
 * early-return pages inside App (form preview, triveny home, /popout/*), and
 * the portal routes main.tsx renders outside App. Every toast entry point in
 * the codebase lands here:
 *   - `import { toast } from 'sonner'`            (direct calls)
 *   - `useToast()` from components/ui/use-toast   (shadcn-style {title, description, variant})
 *
 * sonner itself provides the aria-live region, honours prefers-reduced-motion,
 * and pauses on hover/focus. This wrapper adds theme tracking: the app toggles
 * `dark` on <html> from several places (AuthContext, AccessibilityContext,
 * member preferences), so we follow that class rather than any one context.
 */

import React, { useContext, useEffect, useState } from 'react';
import { Toaster as SonnerToaster, type ToasterProps } from 'sonner';
import TranslationContext from '@/contexts/TranslationContext';

function readTheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

/** Tracks the `dark` class on <html>. */
export function useDocumentTheme(): 'light' | 'dark' {
  const [theme, setTheme] = useState<'light' | 'dark'>(readTheme);
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => setTheme(readTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return theme;
}

export function Toaster(props: ToasterProps) {
  const theme = useDocumentTheme();
  // Read the context directly so the toaster never throws where no
  // TranslationProvider is mounted (tests, isolated widgets).
  const i18n = useContext(TranslationContext);
  const t = (key: string, fallback: string) => (i18n ? i18n.t(key, fallback) : fallback);

  return (
    <SonnerToaster
      theme={theme}
      position="bottom-right"
      richColors
      closeButton
      visibleToasts={4}
      containerAriaLabel={t('notifications.title', 'Notifications')}
      toastOptions={{ closeButtonAriaLabel: t('buttons.close', 'Close') }}
      {...props}
    />
  );
}

export default Toaster;
